import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import type { CKEditorInstance } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import {
  BoldIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  NumberListIcon,
  OpenChatControlsIcon,
  QuoteIcon,
  StrikeThroughIcon,
  UnderlineIcon,
} from '../../bombiix-icon';
import { richTextLength, sanitizeRichTextHtml } from '../../utils/html-text';
import './CommonRichTextField.scss';

export interface CommonRichTextFieldProps {
  /** Label rendered inside the toolbar strip, ahead of the controls. */
  label: string;
  /** Stored value — HTML, or legacy plain text, which CKEditor loads as a paragraph. */
  value: string;
  onChange: (_html: string) => void;
  id?: string;
  placeholder?: string;
  maxLength?: number;
  hideCounter?: boolean;
  /**
   * Editable body height before it scrolls (Figma frame height). A number is
   * treated as pixels; a string is used verbatim so callers can pass a `calc(...)`.
   */
  minHeight?: number | string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
  /** Optional node portalled to the right of the toolbar strip (e.g. a "Not visible to supplier" badge). */
  toolbarSuffix?: ReactNode;
  /** Marks the field mandatory: a red `*` follows the label inside the toolbar strip. */
  required?: boolean;
}

/**
 * Toolbar order mirrors the Figma frame (node 13738:101728) item for item, and
 * matches the bulk-edit canvas comment editor. The frame's fourth glyph — the
 * layer named "Open Chat controls" — is the fontColor "A". Undo/redo are absent
 * from the design, so they are not registered.
 */
const TOOLBAR_ITEMS = ['bold', 'italic', 'underline', 'fontColor', 'strikethrough', '|', 'bulletedList', 'numberedList', '|', 'blockQuote', 'link'];

/**
 * Bombiix master glyphs, serialised once at module load. CKEditor's ButtonView
 * takes an SVG string, so the icon components stay the single source of truth
 * for the artwork instead of the markup being copied into a stylesheet.
 */
const TOOLBAR_ICONS: Record<string, string> = {
  bold: renderToStaticMarkup(<BoldIcon size={24} />),
  italic: renderToStaticMarkup(<ItalicIcon size={24} />),
  underline: renderToStaticMarkup(<UnderlineIcon size={24} />),
  fontColor: renderToStaticMarkup(<OpenChatControlsIcon size={24} />),
  strikethrough: renderToStaticMarkup(<StrikeThroughIcon size={24} />),
  bulletedList: renderToStaticMarkup(<ListIcon size={24} />),
  numberedList: renderToStaticMarkup(<NumberListIcon size={24} />),
  blockQuote: renderToStaticMarkup(<QuoteIcon size={24} />),
  link: renderToStaticMarkup(<LinkIcon size={24} />),
};

const EDITOR_CONFIG = {
  toolbar: TOOLBAR_ITEMS,
  link: {
    defaultProtocol: 'https://',
    addTargetToExternalLinks: true,
  },
  htmlSupport: {
    disallow: [{ name: /^(script|style|iframe|object|embed|form|input)$/ }],
    allow: [{ name: 'a', attributes: ['href', 'target', 'rel'], classes: false, styles: false }],
  },
};

/**
 * Repaints the toolbar with the Bombiix master glyphs. Toolbar views are built
 * in the declared order of `TOOLBAR_ITEMS`, separators included, so the config
 * and the view collection can be walked in lockstep; a dropdown (fontColor)
 * wraps its trigger in `buttonView`.
 *
 * @param {CKEditorInstance} editor - The ready CKEditor instance.
 * @returns {void}
 */
const applyMasterIcons = (editor: CKEditorInstance): void => {
  const items = Array.from(editor.ui.view.toolbar.items);
  TOOLBAR_ITEMS.forEach((name, index) => {
    const glyph = TOOLBAR_ICONS[name];
    const view = items[index];
    if (!glyph || !view) {
      return;
    }
    const button = view.buttonView ?? view;
    button.icon = glyph;
  });
};

/**
 * Bombiix master rich-text field (Figma node 13738:101728): a bordered control
 * whose grey toolbar strip carries the field label alongside the formatting
 * controls, over a white editable body.
 *
 * The value is HTML. It is written into the same `String` columns that used to
 * hold plain text, so legacy plain values load unchanged and every plain-text
 * reader of the same column must run through `htmlToText`.
 *
 * A `maxLength` both caps the field and renders the `used/limit` counter beneath
 * it, measured with the same `richTextLength` the cap uses so the two can never
 * disagree. CKEditor's own WordCount plugin is not in this build, and the build
 * lives under the vendored `src/lib` tree, so the count is computed here.
 */
function CommonRichTextField({
  label,
  value,
  onChange,
  id,
  placeholder = '',
  maxLength,
  hideCounter = false,
  minHeight = 96,
  disabled = false,
  className = '',
  'data-testid': dataTestId,
  toolbarSuffix,
  required = false,
}: CommonRichTextFieldProps) {
  const [toolbarElement, setToolbarElement] = useState<HTMLElement | null>(null);
  /**
   * Last value that satisfied `maxLength`, used to reject an over-long paste.
   * A ref, not state: rejecting must not re-render, because re-rendering the
   * tree re-initialises CKEditor.
   */
  const lastValidRef = useRef<string>(value);

  const editorConfig = useMemo(() => ({ ...EDITOR_CONFIG, placeholder }), [placeholder]);

  /**
   * CKEditor mutates its own DOM after mount, so the label is portalled into the
   * live toolbar element rather than rendered as a sibling and positioned over
   * it. `order: -1` in the stylesheet floats it ahead of the controls.
   *
   * The adapter fires this from a deferred `setTimeout` and passes its own
   * `editor` getter, which returns `null` once the watchdog has been torn down.
   * An unmount between editor creation and that timeout — a closed drawer, a
   * route change, or React StrictMode's dev remount — therefore delivers a null
   * editor, so the instance is checked before it is touched.
   *
   * @param {CKEditorInstance | null} editor - The ready editor, or null when the
   * component was destroyed before the deferred ready callback ran.
   * @returns {void}
   */
  const handleReady = useCallback(
    (editor: CKEditorInstance | null) => {
      if (!editor) {
        return;
      }
      applyMasterIcons(editor);
      lastValidRef.current = editor.getData();
      setToolbarElement(editor.ui.view.toolbar.element);
    },
    [],
  );

  /**
   * Rejects an edit that pushes the visible text past `maxLength` by restoring
   * the last accepted value. CKEditor has no native maxlength, and the cap has
   * to be measured on the text so markup does not eat the user's budget.
   */
  const handleChange = useCallback(
    (_event: unknown, editor: CKEditorInstance) => {
      const data = editor.getData();
      if (maxLength !== undefined && richTextLength(data) > maxLength) {
        editor.setData(lastValidRef.current);
        return;
      }
      lastValidRef.current = data;
      onChange(data);
    },
    [maxLength, onChange],
  );

  const handleBlur = useCallback(
    (_event: unknown, editor: CKEditorInstance) => {
      onChange(sanitizeRichTextHtml(editor.getData()));
    },
    [onChange],
  );

  /*
   * The counter is a sibling of the field, not a child: the root carries the
   * control's border, so a nested counter would sit inside the box. Both pages
   * that hand-rolled this counter placed it the same way.
   */
  return (
    <>
      <div
        id={id}
        className={`bombiix-rich-text ${disabled ? 'bombiix-rich-text--disabled' : ''} ${className}`}
        style={{ '--bombiix-rich-text-body-height': typeof minHeight === 'number' ? `${minHeight}px` : minHeight } as CSSProperties}
        data-testid={dataTestId}
      >
        <CKEditor
          editor={ClassicEditor}
          data={value}
          disabled={disabled}
          config={editorConfig}
          onReady={handleReady}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {toolbarElement
          && createPortal(
            <span className="bombiix-rich-text__label" id={id ? `${id}-label` : undefined}>
              {label}
              {required && (
                <span className="bombiix-rich-text__required" aria-hidden="true">
                  *
                </span>
              )}
            </span>,
            toolbarElement,
          )}
        {toolbarElement
          && toolbarSuffix
          && createPortal(
            <span className="bombiix-rich-text__toolbar-suffix">{toolbarSuffix}</span>,
            toolbarElement,
          )}
      </div>
      {maxLength !== undefined && !hideCounter && (
        <span className="bombiix-rich-text__counter" data-testid={dataTestId ? `${dataTestId}-counter` : undefined}>
          {richTextLength(value)}/{maxLength}
        </span>
      )}
    </>
  );
}

export default CommonRichTextField;
